/**
 * AutoCount — Sign In
 * Real credential login plus a one-click "Enter live demo" for prospects.
 */

import { useState, FormEvent, useEffect, ChangeEvent } from "react";

// react-router-dom components
import { Link, useNavigate, useLocation } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";

// Auth context
import { useAuth } from "context/auth/authContext";

// Images
import bgImage from "assets/images/bg-sign-in-basic.jpeg";

const DEMO_USERNAME = "admin@autocount.com";
const DEMO_PASSWORD = "admin123";

function Basic(): JSX.Element {
  // Auth context to handle login
  const auth = useAuth();
  const navigate = useNavigate();

  // Destructure auth context for easier access
  const { login, authState, isLoading } = auth;
  const { error } = authState;

  // Form state — pre-filled with the demo account so prospects can just click.
  const [username, setUsername] = useState(DEMO_USERNAME);
  const [password, setPassword] = useState(DEMO_PASSWORD);
  const [rememberMe, setRememberMe] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { isAuthenticated } = authState;
  const location = useLocation();

  // Get the redirect path if user was redirected to login
  const from = (location.state as any)?.from?.pathname || "/dashboard";

  // Handle navigation after successful login
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, from, authState]);

  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  const doLogin = async (user: string, pass: string) => {
    if (!user || !pass || isSubmitting || isLoading) {
      return;
    }
    setIsSubmitting(true);
    try {
      await login({ username: user, password: pass });
      // Navigation is handled by useEffect
    } catch (err) {
      // Error state is handled by context and displayed via {error}
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await doLogin(username, password);
  };

  const handleDemo = async () => {
    setUsername(DEMO_USERNAME);
    setPassword(DEMO_PASSWORD);
    await doLogin(DEMO_USERNAME, DEMO_PASSWORD);
  };

  const busy = isSubmitting || isLoading;

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={3}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Sign in to AutoCount
          </MDTypography>
          <MDTypography display="block" variant="button" color="white" my={1}>
            Barcode-driven inventory for auto parts
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          {error && (
            <MDBox mb={2}>
              <Alert severity="error" onClose={auth.clearError}>
                {error}
              </Alert>
            </MDBox>
          )}

          {/* One-click demo entry for prospects */}
          <MDButton
            variant="gradient"
            color="success"
            fullWidth
            onClick={handleDemo}
            disabled={busy}
            startIcon={!busy ? <Icon>bolt</Icon> : undefined}
          >
            {busy ? <CircularProgress size={22} color="inherit" /> : "Enter live demo"}
          </MDButton>
          <MDBox mt={1} textAlign="center">
            <MDTypography variant="caption" color="text">
              Instant access — no sign-up required
            </MDTypography>
          </MDBox>

          <MDBox my={2} display="flex" alignItems="center">
            <Divider sx={{ flex: 1 }} />
            <MDTypography variant="caption" color="text" px={1.5}>
              or sign in
            </MDTypography>
            <Divider sx={{ flex: 1 }} />
          </MDBox>

          <MDBox component="form" role="form" onSubmit={handleSubmit}>
            <MDBox mb={2}>
              <MDInput
                type="text"
                label="Username"
                fullWidth
                value={username}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setUsername(e.target.value)}
                required
              />
            </MDBox>
            <MDBox mb={1}>
              <MDInput
                type="password"
                label="Password"
                fullWidth
                value={password}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                required
              />
            </MDBox>
            <MDBox mb={1}>
              <MDTypography variant="caption" color="text">
                Demo credentials&nbsp;·&nbsp;
                <MDTypography component="span" variant="caption" fontWeight="medium" color="info">
                  {DEMO_USERNAME}
                </MDTypography>
                &nbsp;/&nbsp;
                <MDTypography component="span" variant="caption" fontWeight="medium" color="info">
                  {DEMO_PASSWORD}
                </MDTypography>
              </MDTypography>
            </MDBox>
            <MDBox display="flex" alignItems="center" ml={-1}>
              <Switch checked={rememberMe} onChange={handleSetRememberMe} />
              <MDTypography
                variant="button"
                fontWeight="regular"
                color="text"
                onClick={handleSetRememberMe}
                sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
              >
                &nbsp;&nbsp;Remember me
              </MDTypography>
            </MDBox>
            <MDBox mt={3} mb={1}>
              <MDButton
                variant="gradient"
                color="info"
                fullWidth
                type="submit"
                disabled={busy}
              >
                {busy ? <CircularProgress size={24} color="inherit" /> : "Sign In"}
              </MDButton>
            </MDBox>
            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                Don&apos;t have an account?{" "}
                <MDTypography
                  component={Link}
                  to="/authentication/sign-up/cover"
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                >
                  Sign up
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default Basic;
